/**
 * SubscriptionGuard Component
 * Protects content that requires an active subscription
 */

import { ReactNode } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { useAdmin } from '@/hooks/useAdmin';
import { Paywall } from './Paywall';
import { Loader2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

interface SubscriptionGuardProps {
    children: ReactNode;
    /** Show paywall in a modal instead of replacing content */
    modal?: boolean;
    /** Fallback content when not subscribed (if not using paywall) */
    fallback?: ReactNode;
    /** Skip subscription check (useful for previews) */
    bypass?: boolean;
    /** Allow Alumni users to access if it's forum content */
    isForum?: boolean;
}

export function SubscriptionGuard({
    children,
    modal = false,
    fallback,
    bypass = false,
    isForum = false
}: SubscriptionGuardProps) {
    const { isLoading: subLoading, isSubscribed, isAlumni, isAdmin: subIsAdmin } = useSubscription();
    const { isAdmin, loading: adminLoading } = useAdmin();

    const isActuallyAdmin = isAdmin || subIsAdmin;

    // Bypass subscription check
    if (bypass) {
        return <>{children}</>;
    }

    // Loading state
    if (subLoading || adminLoading) {
        return (
            <div className="flex flex-col items-center justify-center p-12 min-h-[300px]">
                <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground animate-pulse">Verificando acceso premium...</p>
            </div>
        );
    }

    // Access granted if subscribed, admin, or special forum alumni access
    if (isSubscribed || isActuallyAdmin || (isForum && isAlumni)) {
        return <>{children}</>;
    }

    // Not subscribed - show paywall or fallback
    if (modal) {
        return (
            <>
                {children}
                <Dialog open={!isSubscribed}>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="sr-only">Suscripción Premium</DialogTitle>
                            <DialogDescription className="sr-only">
                                Suscríbete para acceder a todo el contenido
                            </DialogDescription>
                        </DialogHeader>
                        <Paywall />
                    </DialogContent>
                </Dialog>
            </>
        );
    }

    // Show fallback or paywall
    if (fallback) {
        return <>{fallback}</>;
    }

    return <Paywall />;
}

/**
 * Hook to check subscription in components
 * Returns a function to wrap premium content
 */
export function usePremiumContent() {
    const { isSubscribed, isLoading } = useSubscription();

    const requiresPremium = (content: ReactNode, fallback?: ReactNode): ReactNode => {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center p-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                </div>
            );
        }

        if (isSubscribed) {
            return content;
        }

        return fallback || (
            <div className="p-4 bg-muted rounded-lg text-center">
                <p className="text-muted-foreground">Contenido Premium</p>
            </div>
        );
    };

    return { isSubscribed, isLoading, requiresPremium };
}
